import React, { useState, useEffect } from 'react';
import { Button, Form, Spinner, Image, Modal } from 'react-bootstrap';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import '../ForgotPassword/ForgotPassword.scss';
import './SetPassword.scss';
import { resetPassword, validateSetPasswordToken } from '../../../Context/actions/authActions';
import { loginImageService } from '../../../services/authServices';

const PASSWORD_MIN_LENGTH = 8;

const INVALID_LINK_MESSAGE = {
    expired: 'This link has expired.',
    used: 'This link has already been used.',
    invalid: 'This link is not valid.',
};

const SetPassword = (props) => {
    const schema = yup.object().shape({
        // required() before min()/oneOf(): yup reports only the first failing test, and an empty
        // box should say it is empty rather than that it is too short.
        password: yup
            .string()
            .required('Password is required')
            .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`),
        confirmPassword: yup
            .string()
            .required('Please confirm your password')
            .oneOf([yup.ref('password')], 'The passwords you have entered do not match. Please try again.'),
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({ resolver: yupResolver(schema) });
    const [loading, setLoadingState] = useState(true);
    const [tokenState, setTokenState] = useState({ checked: false, valid: false, reason: 'invalid', email: '' });
    const [successMessage, setSuccessMessage] = useState(false);
    const [submitError, setSubmitError] = useState('');
    // This page is mounted outside the auth context provider, like the other /p/ routes, so it
    // reads the banner image straight from the service rather than through an action.
    const [loginImageURL, setLoginImageURL] = useState('');

    const token = new URLSearchParams(props.location.search).get('token');

    // The banner is decorative, so it is fetched on its own: a failing image request must not be
    // mistaken for a failing token and render a working link as expired.
    useEffect(() => {
        (async () => {
            try {
                const image = await loginImageService();
                setLoginImageURL(image?.data?.customerPortalLoginImageURL || '');
            } catch (e) {
                console.log(e);
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const result = token ? await validateSetPasswordToken(token) : { valid: false, reason: 'invalid' };
                setTokenState({
                    checked: true,
                    valid: Boolean(result.valid),
                    reason: result.reason || 'invalid',
                    email: result.email || '',
                });
            } catch (e) {
                console.log(e);
                setTokenState({ checked: true, valid: false, reason: 'invalid', email: '' });
            }
            setLoadingState(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = handleSubmit(async (data) => {
        setLoadingState(true);
        setSubmitError('');
        try {
            await resetPassword({ token, password: data.password });
            setSuccessMessage(true);
        } catch (error) {
            const reason = error?.response?.data?.reason;
            setSubmitError(INVALID_LINK_MESSAGE[reason] || 'We could not update your password. Please try again.');
            if (reason) setTokenState((prev) => ({ ...prev, valid: false, reason }));
        }
        setLoadingState(false);
    });

    const goToLogin = () => props.history.push('/auth/login');
    const goToForgotPassword = () => props.history.push('/auth/forgot-password');

    const renderBody = () => {
        if (successMessage) {
            return (
                <div className="box-input d-flex justify-content-center">
                    <h3>PASSWORD SET</h3>
                    <h6>Your password has been updated. You can now sign in with it.</h6>
                    <div className="w-100">
                        <div className="sign-up-button">
                            <Button id="backToLogin" onClick={goToLogin} variant="info">
                                BACK TO LOGIN
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        if (tokenState.checked && !tokenState.valid) {
            return (
                <div className="box-input d-flex justify-content-center">
                    <h3>LINK NO LONGER VALID</h3>
                    <h6>
                        {INVALID_LINK_MESSAGE[tokenState.reason] || INVALID_LINK_MESSAGE.invalid} Request a new one to
                        choose your password.
                    </h6>
                    <div className="w-100">
                        <div className="sign-up-button">
                            <Button id="requestNewLink" onClick={goToForgotPassword} variant="info">
                                REQUEST A NEW LINK
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="box-input d-flex justify-content-center">
                <h3>CHOOSE YOUR PASSWORD</h3>
                <h6>{tokenState.email ? `Setting the password for ${tokenState.email}.` : 'Enter a new password.'}</h6>
                <Form className="w-100" onSubmit={onSubmit}>
                    <div className="form-input">
                        <Form.Group>
                            <Form.Control
                                type="password"
                                placeholder="New password"
                                name="password"
                                autoComplete="new-password"
                                isInvalid={Boolean(errors?.password)}
                                {...register('password')}
                            />
                            <Form.Control.Feedback type="invalid">{errors?.password?.message}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group>
                            <Form.Control
                                type="password"
                                placeholder="Confirm new password"
                                name="confirmPassword"
                                autoComplete="new-password"
                                isInvalid={Boolean(errors?.confirmPassword)}
                                {...register('confirmPassword')}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors?.confirmPassword?.message}
                            </Form.Control.Feedback>
                        </Form.Group>
                        {submitError && <p className="text-danger">{submitError}</p>}
                    </div>
                    <div className="w-100">
                        <div className="sign-up-button">
                            <Button id="setPassword" type="submit" variant="info">
                                SET PASSWORD
                            </Button>
                        </div>
                    </div>
                </Form>
            </div>
        );
    };

    return (
        <>
            <div className="forgot-page-wrapper set-password-page">
                <div className="d-flex forgot-box">
                    <div className="images-wrap">
                        <Image src={loginImageURL} style={{ width: '100%', height: 'auto' }} />
                    </div>
                    <div className="login-box-content">
                        <div className="box-content w-100">{renderBody()}</div>
                    </div>
                </div>
            </div>
            {loading && (
                <Modal transition="false" show={true} className="loading-modal text-center">
                    <Spinner animation="border" variant="primary" />
                </Modal>
            )}
        </>
    );
};

export default SetPassword;
